import React, {Fragment, useCallback, useEffect, useMemo, useState} from 'react';
import {fetchMigrationSendersList, fetchSendersList, migrateSender} from "../services";
import FormGroupRowSender from "../Elements/FormGroupRowSender";
import Table from "../Elements/Table/Table";
import Select from "react-select";
import {Button} from "reactstrap";

const availablePaymentMethods = [
    'bancontact',
    'payconiq',
    'ideal',
    'digicash',
    'maestro',
    'visa',
    'mastercard',
    'cartebancaire',
    'cartebleuevisa',
    'payByBank',
    'applepay',
    'paypal',
];

const MigrationOverview = () => {

    const [senderId, setSenderId] = useState(undefined);
    const [parentSenderList, setParentSenderList] = useState([])
    const [childSenderList, setChildSenderList] = useState([])
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchSendersList({isParent: true})
            .then(list => {
                setParentSenderList(list)
            })
            .catch(error => {
                console.error(error);
                setParentSenderList([]);
            });
    }, [])

    useEffect(() => {
        if (senderId !== undefined) {
            fetchMigrationSendersList({parentId: senderId})
                .then(list => {
                    const sortedList = list.map(sender => {
                        const pm = (sender.paymentMethods?.split(',') ?? [])
                            .filter(method => availablePaymentMethods.includes(method))
                            .sort((a, b) => availablePaymentMethods.indexOf(a) - availablePaymentMethods.indexOf(b))
                        const hasPayOnce = sender.paymentMethods?.split(',').includes('pay-once-bc') || false
                        sender.payDirectlyPaymentMethods = pm
                        sender.hasPayOnce = hasPayOnce
                        return sender
                    }).sort((a, b) => a.senderContractNumber.localeCompare(b.senderContractNumber));
                    setChildSenderList(sortedList);
                })
                .catch(error => {
                    console.error(error);
                    setChildSenderList([]);
                })
        }
    }, [senderId])

    const handleCheckAll = useCallback((newValue) => {
        setChildSenderList((prev) => {
            return prev.map(child => ({...child, checked: newValue}));
        })
    }, [])

    const tableColumns = useMemo(() => ({
        checkbox: {
            name: (
                <input
                    type="checkbox"
                    checked={childSenderList?.length && childSenderList.every(child => child.checked)}
                    onChange={event => handleCheckAll(event.target.checked)}
                />
            ),
            style: {
                width: '45px',
            },
        },
        id: {
            name: 'Sender Id',
            style: {
                width: '150px',
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        senderContractNumber: {
            name: 'Sender Contract Number',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        name: {
            name: 'Sender Name',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        senderState: {
            name: 'State',
            style: {
                fontSize: '9pt',
                textAlign: 'center',
            },
            rowStyleFn: (item) => ({
                fontSize: '9pt',
                textAlign: 'center',
                backgroundColor: item.senderState === 'ANTIGOON' ? '#71A404' : 'inherit',
                color: item.senderState === 'ANTIGOON' ? 'white' : 'inherit'
            })
        },
        beneficiaryReferenceId: {
            name: 'Beneficiary Reference Id',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        paymentFlowCodeInput: {
            name: 'Payment Flow Code',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        vatNumberInput: {
            name: 'VAT Number',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        emailInput: {
            name: 'Email',
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        addressInput: {
            name: "Address",
            style: {
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
        paymentMethodsInput: {
            name: "Payment Methods & POD",
            style: {
                width: '200px',
                fontSize: '9pt',
            },
            rowStyle: {
                fontSize: '9pt',
            }
        },
    }), [childSenderList, handleCheckAll]);

    const handleOnChange = useCallback((item, field, newValue) => {
        setChildSenderList((prev) => {
            const others = prev.filter(child => child.id !== item.id)
            item[field] = newValue
            return [...others, item].sort((a, b) => a.senderContractNumber.localeCompare(b.senderContractNumber))
        })
    }, [])

    const handlePaste = useCallback((event) => {
        setChildSenderList((prev) => {
            let clipboardData = event.clipboardData.getData('Text').trim();
            if (clipboardData.includes("\t") && clipboardData.split("\t").length > 7) {
                event.preventDefault();
                const newList = [...prev]
                clipboardData.split("\n").forEach(data => {
                    data.split("\r").forEach(line => {
                        if (line.includes("\t") && line.split("\t").length > 7) {
                            const lineData = line.split("\t")
                            const offset = lineData[0].startsWith('ADD') ? -1 : 0
                            const item = newList.find(item => item.senderContractNumber === lineData[1 + offset])
                            if (item) {
                                item.paymentFlowCode = offset === -1 ? "" : lineData[0]
                                item.address = lineData[4 + offset]
                                item.vatNumber = lineData[5 + offset]
                                item.email = lineData[7 + offset]
                            }
                        }
                    })
                })
                return newList
            }
        })
    }, [])

    const childSenderListWithInputs = useMemo(() => {
        return childSenderList?.map(item => {
            const checked = item.checked ?? false
            return {
                ...item,
                key: item.id,
                checked: checked,
                checkbox: (
                    <input
                        type="checkbox"
                        checked={checked}
                        onChange={event => handleOnChange(item, 'checked', event.target.checked)}
                    />
                ),
                paymentFlowCodeInput: (
                    <input type="text" value={item.paymentFlowCode ?? ""}
                           style={{width: '100%', border: '1px solid #ccc', borderRadius: '3px', padding: '2px'}}
                           onChange={event => handleOnChange(item, 'paymentFlowCode', event.target.value)}
                           onPaste={handlePaste}
                    />
                ),
                vatNumberInput: (
                    <input type="text" value={item.vatNumber ?? ""}
                           style={{width: '100%', border: '1px solid #ccc', borderRadius: '3px', padding: '2px'}}
                           onChange={event => handleOnChange(item, 'vatNumber', event.target.value)}
                    />
                ),
                emailInput: (
                    <input type="text" value={item.email ?? ""}
                           style={{width: '100%', border: '1px solid #ccc', borderRadius: '3px', padding: '2px'}}
                           onChange={event => handleOnChange(item, 'email', event.target.value)}
                    />
                ),
                addressInput: (
                    <input type="text" value={item.address ?? ""}
                           style={{width: '100%', border: '1px solid #ccc', borderRadius: '3px', padding: '2px'}}
                           onChange={event => handleOnChange(item, 'address', event.target.value)}
                    />
                ),
                paymentMethodsInput: (
                    <>
                        <Select
                            value={item.payDirectlyPaymentMethods.map(method => ({
                                value: method,
                                label: method
                            }))}
                            onChange={newSelectedMethods => {
                                handleOnChange(item, 'payDirectlyPaymentMethods', newSelectedMethods
                                    .map(method => method.value)
                                    .sort((a, b) => availablePaymentMethods.indexOf(a) - availablePaymentMethods.indexOf(b))
                                );
                            }}
                            options={availablePaymentMethods.map(method => ({value: method, label: method}))}
                            multi
                            clearable={false}
                        />
                        <div style={{display: "flex", gap: "3px", alignItems: "center", marginTop: "5px"}}>
                            <label style={{margin: 0}}>POD: </label>
                            <input type="checkbox" checked={item.hasPayOnce}
                                   onChange={event => handleOnChange(item, 'hasPayOnce', event.target.checked)}
                            />
                        </div>
                    </>
                )
            }
        }) ?? []
    }, [childSenderList, handlePaste, handleOnChange])

    const handleMigrate = useCallback(() => {
        const selected = childSenderList.filter(child => child.checked)
        let interrupt = false
        setBusy(true)
        setError(null)
        selected.forEach(sender => {
            if (!interrupt) {
                const params = {
                    paymentFlowCode: sender.paymentFlowCode,
                    vatNumber: sender.vatNumber,
                    email: sender.email,
                    addressLine: sender.address,
                    payDirectlyPaymentMethods: sender.payDirectlyPaymentMethods,
                    hasPayOnceBC: sender.hasPayOnce
                }

                migrateSender(sender.id, params)
                    .then(data => {
                        setChildSenderList((prev) => {
                            const others = prev.filter(child => child.id !== sender.id)
                            sender.senderState = data.senderState;
                            sender.beneficiaryReferenceId = data.beneficiaryReferenceId;
                            return [...others, sender].sort((a, b) => a.senderContractNumber.localeCompare(b.senderContractNumber))
                        })
                    })
                    .catch(error => {
                        console.error("Error migrating sender", {error})
                        setError(error)
                        interrupt = true
                    });
            }
        })
        setBusy(false)
    }, [childSenderList])

    return (
        <Fragment>
            <div style={{display: "flex", gap: "10px", width: "100%", alignItems: "center", marginBottom: "10px"}}>
                <FormGroupRowSender
                    label="Parent Sender"
                    value={senderId}
                    onChange={setSenderId}
                    senders={parentSenderList}
                    wrapperStyle={{width: '100%', margin: 0}}
                />
                <Button children={busy ? "BUSY" : "Migrate"} color="primary" size="sm" onClick={handleMigrate}/>
            </div>
            {error && (<div className="alert alert-danger">
                {error}
            </div>)}
            {childSenderList?.length && (<Table
                columns={tableColumns}
                items={childSenderListWithInputs}
                location={window.history.location}
            />)}
        </Fragment>
    );
};

export default MigrationOverview;